import { useRef, useState } from "react";
import moment from "moment-timezone";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { CommonDestinationTimezone, MimeType } from "@/lib/api/utils";
import { downloadBinaryData } from "@/lib/browser/download";
import { parseFromFile } from "@/lib/excel";
import { UploadPresignedFile, UploadPresignedBlob } from "@/lib/file";
import { fetchReportCropProfiles } from "@/lib/api/risk/profiles";
import { createTrackedReport } from "@/lib/api/trackedReports";
import {
  buildOcNcWorkbook,
  findProducerRiskTabs,
  ProcessResult,
} from "./producerRiskOcNc/process";

interface CompletedRun {
  result: ProcessResult;
  // Presigned GET-style direct link the user can re-download from if the
  // automatic download was blocked by the browser. We expose a blob URL
  // (NOT the S3 URL) so it works after upload regardless of bucket CORS.
  downloadBlobUrl: string;
  downloadFilename: string;
}

export function ProducerRiskOcNcReport(): JSX.Element {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [completed, setCompleted] = useState<CompletedRun | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onProcess = async () => {
    if (!file || isProcessing) return;

    // Clear any previous completed run so the user always sees the
    // freshest result (and a stale blob URL doesn't leak across runs).
    if (completed) {
      URL.revokeObjectURL(completed.downloadBlobUrl);
      setCompleted(null);
    }

    setIsProcessing(true);
    try {
      // 1. Parse the source xlsx.
      const parsed = await parseFromFile(file);
      if (!parsed.success || !parsed.data) {
        throw new Error(parsed.error?.toString() || "Failed to parse uploaded file");
      }
      const workbook = parsed.data.excelJsWorkbook;

      // 2. Locate the Producer Risk tab.
      const matches = findProducerRiskTabs(workbook);
      if (matches.length === 0) {
        toast({
          title: "No Producer Risk tab found",
          description: "The uploaded workbook does not contain a tab whose name contains \"Producer Risk\".",
          variant: "destructive",
        });
        return;
      }
      if (matches.length > 1) {
        toast({
          title: "Multiple Producer Risk tabs found",
          description: `Found ${matches.length} tabs matching \"Producer Risk\". Please supply a workbook with exactly one.`,
          variant: "destructive",
        });
        return;
      }
      const producerRiskSheet = matches[0]!;

      // 3. Crop years used by the report.
      const ncYear = moment.tz(CommonDestinationTimezone).year();
      const ocYear = ncYear - 1;

      // 4. Fetch crop profiles (single call, both years).
      const profilesResult = await fetchReportCropProfiles([ocYear, ncYear]);
      if (!profilesResult.success) {
        toast({
          title: "Failed to load risk crop profiles",
          description: profilesResult.message || "Please try again.",
          variant: "destructive",
        });
        return;
      }
      const profiles = profilesResult.data.profiles;

      // 5. Build the generated workbook. We do NOT mutate the parsed
      //    source workbook; ExcelComposer produces a fresh output.
      const processResult = await buildOcNcWorkbook({
        producerRiskSheet,
        profiles,
        ncYear,
        ocYear,
      });

      // 6. Create the tracked report (presigned PUT urls).
      const sourceFilename = file.name;
      const dateStamp = moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD");
      const outputFilename = `producer-risk-oc-nc-${dateStamp}.xlsx`;

      const createResult = await createTrackedReport({
        report_type: "producer_risk_oc_nc",
        files: {
          source: [{ filename: sourceFilename, file_type: MimeType.Xlsx }],
          output: [{ filename: outputFilename, file_type: MimeType.Xlsx }],
        },
      });
      if (!createResult.success) {
        toast({
          title: "Failed to create tracked report",
          description: createResult.message || "Please try again.",
          variant: "destructive",
        });
        return;
      }
      const sourceTarget = createResult.data.source[0];
      const outputTarget = createResult.data.output[0];
      if (!sourceTarget || !outputTarget) {
        toast({
          title: "Tracked report response was malformed",
          description: "Missing source or output presigned URL.",
          variant: "destructive",
        });
        return;
      }

      // 7. Upload source file. Abort on failure (no output upload, no
      //    download \u2014 spec is explicit about this).
      const sourceUpload = await UploadPresignedFile(sourceTarget.put_url, file, MimeType.Xlsx);
      if (!sourceUpload.success) {
        toast({
          title: "Failed to upload source file",
          description: "The source file could not be uploaded to S3. Aborting.",
          variant: "destructive",
        });
        return;
      }

      // 8. Upload generated workbook. If this fails, do NOT auto-download
      //    and do NOT show a fallback link \u2014 the run is aborted.
      const outputBlob = new Blob([processResult.buffer], { type: MimeType.Xlsx });
      const outputUpload = await UploadPresignedBlob(outputTarget.put_url, outputBlob, MimeType.Xlsx);
      if (!outputUpload.success) {
        toast({
          title: "Failed to upload generated report",
          description: "The generated report could not be uploaded to S3. Aborting.",
          variant: "destructive",
        });
        return;
      }

      // 9. Both uploads succeeded \u2014 download + show fallback link.
      downloadBinaryData(processResult.buffer, outputFilename, MimeType.Xlsx);
      const blobUrl = URL.createObjectURL(outputBlob);
      setCompleted({
        result: processResult,
        downloadBlobUrl: blobUrl,
        downloadFilename: outputFilename,
      });

      toast({
        title: "Producer Risk report generated",
        description: `${processResult.counts.ncRows} NC row(s), ${processResult.counts.ocRows} OC row(s).`,
      });
    } catch (error: unknown) {
      console.error("Failed to process Producer Risk report", error);
      toast({
        title: "Failed to process report",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFile(null);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Producer Risk</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Upload a Producer Risk export. The workbook must contain exactly
            one tab whose name contains "Producer Risk" (case-insensitive). The
            generated report will have tabs: NC, OC, NC summary, OC summary, Acres.
          </p>

          <div className="space-y-2">
            <Label htmlFor="producer-risk-file">Producer Risk Workbook (.xlsx)</Label>
            <Input
              id="producer-risk-file"
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              disabled={isProcessing}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <Button
            onClick={onProcess}
            disabled={!file || isProcessing}
            className="w-full"
          >
            {isProcessing ? (
              <>
                Processing...
              </>
            ) : (
              "Process Report"
            )}
          </Button>

          {completed && <CompletedSummary completed={completed} />}
        </CardContent>
      </Card>
    </div>
  );
}

function CompletedSummary({ completed }: { completed: CompletedRun }): JSX.Element {
  const { result, downloadBlobUrl, downloadFilename } = completed;
  return (
    <div className="space-y-4 border rounded-md p-4 bg-muted/30">
      <div className="flex flex-wrap gap-4 text-sm">
        <StatTile label="Total data rows" value={result.counts.totalDataRows.toString()} />
        <StatTile label="NC rows" value={result.counts.ncRows.toString()} />
        <StatTile label="OC rows" value={result.counts.ocRows.toString()} />
        <StatTile label="Unknown commodity" value={result.counts.skippedUnknownCommodity.toString()} />
        <StatTile label="Invalid dates" value={result.counts.skippedInvalidDate.toString()} />
        <StatTile label="NC year" value={result.ncYear.toString()} />
        <StatTile label="OC year" value={result.ocYear.toString()} />
      </div>

      <div>
        <a
          href={downloadBlobUrl}
          download={downloadFilename}
          className="text-sm font-medium text-primary underline"
        >
          Re-download generated workbook
        </a>
      </div>

      {result.errors.unknownCommodities.length > 0 && (
        <ErrorList
          title="Unknown commodities (skipped)"
          items={result.errors.unknownCommodities.map(
            (e: { row: number; commodity: string; customer: string }) =>
              `Row ${e.row}: ${e.commodity || "(blank)"} \u2014 ${e.customer || "(blank customer)"}`,
          )}
        />
      )}
      {result.errors.invalidDates.length > 0 && (
        <ErrorList
          title="Invalid delivery end dates (skipped)"
          items={result.errors.invalidDates.map(
            (e: { row: number; commodity: string; customer: string; raw: string }) =>
              `Row ${e.row}: ${e.commodity} for ${e.customer} (raw: ${JSON.stringify(e.raw)})`,
          )}
        />
      )}
      {result.errors.unknownCustomers.length > 0 && (
        <ErrorList
          title="Customers with no risk profiles"
          items={result.errors.unknownCustomers}
        />
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function ErrorList({ title, items }: { title: string; items: string[] }): JSX.Element {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1">{title}</h4>
      <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5">
        {items.map((item: string, idx: number) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

